import { ReactNode } from 'react';
import { NavLink, Link, useLocation } from 'react-router-dom';
import { Ticket, MailCheck, ShieldCheck, SlidersHorizontal, ScrollText, Settings as SettingsIcon, LogOut, ClipboardCheck } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useAccessiblePages } from '../hooks/useAccessiblePages';
import { useOpsStats } from '../hooks/useAuditApi';
import { SkeletonBar } from './ui/Skeleton';

import { Button } from './ui/Button';
import { WhatsNewButton, NoticeBar } from '../whats-new';
/** Non-terminal ticket statuses — mirrors tickets/list.ts's DEFAULT_STATUSES,
 *  what a reviewer means by "still open" rather than every row ever created. */
const OPEN_TICKET_STATUSES = ['New', 'NeedsReview', 'AwaitingSalesApproval', 'AwaitingApproval', 'AwaitingSupplier'];

/**
 * Roman's ops strip: the open workload at a glance, each count linking to
 * where it gets worked. Deliberately just three counts — the H38 KPI set
 * belongs to Marcello, not here.
 */
function OpsStrip() {
  const { data: stats } = useOpsStats();

  /*
   * P38, task 5 - reference §12: figures are DASHES while loading, never zeros.
   *
   * This used to `return null` until the counts arrived. That avoids the zero
   * honestly and costs the reader a 41 px band appearing a second after they
   * started reading: the header grows and the page moves down under their eyes.
   *
   * An em dash occupies the space and claims nothing. A zero would be a
   * measurement, and nothing has been measured yet - the same error as the
   * headline that read "0 open" with 41 open (audit S1-03), produced by
   * impatience instead of by a filter.
   */
  const figure = (n: number | undefined) => (stats ? String(n ?? 0) : '—');
  const openTickets = stats
    ? OPEN_TICKET_STATUSES.reduce((sum, status) => sum + (stats.ticketsByStatus[status] ?? 0), 0)
    : undefined;

  return (
    <div className="flex items-center gap-4 border-b border-gray-200 bg-white px-6 py-2 text-sm">
      <Link to="/tickets" className="flex items-center gap-1.5 text-gray-700 hover:text-info">
        <span className="font-semibold text-ink tabular-nums">{figure(openTickets)}</span> open tickets
      </Link>
      <span className="text-hair" aria-hidden="true">·</span>
      <Link to="/authorizations" className="flex items-center gap-1.5 text-gray-700 hover:text-info">
        <span className="font-semibold text-ink tabular-nums">{figure(stats?.pendingAuthorizations)}</span> pending authorizations
      </Link>
      <span className="text-hair" aria-hidden="true">·</span>
      <Link to="/approvals" className="flex items-center gap-1.5 text-gray-700 hover:text-info">
        <span className="font-semibold text-ink tabular-nums">{figure(stats?.pendingApprovals)}</span> pending approvals
      </Link>
      <span className="text-hair" aria-hidden="true">·</span>
      {/* The review count comes from the SERVER, not from summing
          `ticketsByStatus`: three live tickets carry an open review reason
          outside `NeedsReview`, and one sits in `NeedsReview` with no reason.
          A counter that disagreed with the page it links to would be worse
          than no counter. */}
      <Link
        to="/review"
        className="flex items-center gap-1.5 text-gray-700 hover:text-info"
        title="Tickets the agent stopped on, plus tickets still carrying an open question."
      >
        <span className="font-semibold text-ink tabular-nums">{figure(stats?.ticketsInReview)}</span> in review
      </Link>
      {/* Pushed to the right edge: it is not a counter and does not belong in the reading run of
          four figures. The dot on it is the only thing in this band that is about the APP rather
          than about the work. */}
      <WhatsNewButton className="ml-auto" />
    </div>
  );
}

/**
 * Sidebar order follows the ORDER OF THE PROCESS, not the alphabet and not the
 * order the pages were built in: a request becomes a ticket, Sales clears the
 * customer, Regulatory approves the reply. Authorizations therefore sits ahead
 * of Approvals — a draft cannot exist before the authorization question is
 * settled (agent/steps/compose-draft.ts declines to compose while a ticket is
 * `AwaitingSalesApproval`), so a reader going top to bottom sees the stages in
 * the order a request actually passes through them. Masters and Settings are
 * configuration, not stages, and stay at the bottom with the ledger between.
 *
 * WITH ONE DELIBERATE EXCEPTION, FIRST (Roman, 2026-09-02, after P31). Review
 * is not a stage of the process — it is the WORK, and it is where a reviewer
 * starts their day. The process order still governs everything below it, and
 * Review's own position inside that order (between "may it go out" and "is this
 * reply good to send") is why it used to sit third. Promoting it says something
 * different and true: `/tickets` is the archive of every request, `/review` is
 * the queue of the ones waiting on a person. The default landing page is
 * unaffected — this is the reading order of the sidebar, not a redirect.
 *
 * =========================================================================
 * TWO GROUPS, SEPARATED BY DISTANCE RATHER THAN BY A LINE (Roman, 2026-09-02)
 * =========================================================================
 * Seven equal-looking rows said nothing about where a person spends their day.
 * Masters, Audit Trail and Settings are opened a few times a month; the four
 * above them are opened every hour. So the configuration group is pinned to the
 * BOTTOM of the sidebar, above the sign-out block, under a quiet
 * `CONFIGURATION` heading — the empty space between the two groups is the whole
 * signal, and it is a stronger one than a rule would be, because it also makes
 * the work group the visual body of the page.
 *
 * They are NOT greyed down or shrunk. Masters is where Katia's wording lands,
 * and that wording is the top practical blocker of the pilot — a group that
 * reads as unimportant would be wrong about exactly the page that matters most
 * right now. Position says "rarely"; type size would have said "minor".
 *
 * `group` is a property of the ITEM, not of its index, because the sidebar is
 * already filtered by permission before it is rendered: Settings is Roman's
 * alone and Masters is Regulatory's, so a Viewer's configuration group holds
 * one row and a hypothetical role with none holds zero. Splitting on a position
 * would put the heading over an empty block for somebody. The heading and the
 * rule render only when the group has something in it.
 */
type NavGroup = 'work' | 'config';

const NAV_ITEMS: Array<{ to: string; label: string; icon: typeof Ticket; page: string; group: NavGroup }> = [
  // First on purpose — see the header. Review rides on the `/tickets` grant, so
  // it appears for exactly the people who can already see tickets, and putting
  // it above Tickets changes who sees what for nobody.
  { to: '/review', label: 'Review', icon: ClipboardCheck, page: '/tickets', group: 'work' },
  { to: '/tickets', label: 'Tickets', icon: Ticket, page: '/tickets', group: 'work' },
  { to: '/authorizations', label: 'Authorizations', icon: ShieldCheck, page: '/authorizations', group: 'work' },
  { to: '/approvals', label: 'Approvals', icon: MailCheck, page: '/approvals', group: 'work' },
  { to: '/masters', label: 'Masters', icon: SlidersHorizontal, page: '/masters', group: 'config' },
  { to: '/audit-trail', label: 'Audit Trail', icon: ScrollText, page: '/audit-trail', group: 'config' },
  { to: '/settings', label: 'Settings', icon: SettingsIcon, page: '/settings', group: 'config' },
];

/**
 * One sidebar row. Extracted when the nav became two groups (2026-09-02): the
 * groups differ in WHERE they sit, never in how a row looks or behaves, and two
 * copies of this markup would have been the way that stops being true.
 */
/**
 * THE SIDEBAR ENTRY THAT THREW THE FILTER AWAY (P38, task 9 - Roman's K9)
 *
 * =========================================================================
 * WHAT KATIA ASKED, AND WHAT WAS ACTUALLY WRONG
 * =========================================================================
 * "Der Filter verschwindet, sobald ich in einem anderen Feld bin. Kann man diesen
 * fixieren?"
 *
 * Measured live on 2026-09-30, and the measurement is what makes this a small fix
 * rather than a policy change: the deep link, the back link from a ticket, a click
 * into the search box, typing in it, the funnel menu and a reload ALL keep the
 * filter. `/tickets?companyCode=dch` shows 15 rows and keeps showing 15. The
 * sidebar `NavLink` is the ONE thing that drops it - a static `to="/tickets"` - so
 * pressing the entry for the board you are already looking at resets it, and 41
 * rows come back with nothing on screen to say why.
 *
 * =========================================================================
 * WHY `sessionStorage`, WHICH P30 FORBADE FOR `localStorage`
 * =========================================================================
 * P30's rule is that a filter is never kept in `localStorage`, and its reason is
 * exact: "a queue that silently shortens itself is worse than no memory at all."
 * A reader who opens the board next week and sees 15 of 41 tickets under a filter
 * they have forgotten is being lied to about their own workload.
 *
 * `sessionStorage` narrows that rule rather than reversing it. It lives for the
 * browser tab: the filter survives the walk to a ticket and back and the click on
 * the sidebar, and a new session starts clean, which is the property P30 was
 * protecting. One key per route, because `/review` and `/tickets` are different
 * questions and P31 made them different pages.
 *
 * What is stored is the URL the reader last had on that board, which already
 * carries the server filters, the sort and the column filters (P30's
 * `toUrlString`). No new state exists; this remembers an address.
 */
const NAV_MEMORY_PREFIX = 'dra-nav-last:';

/** Remember a board's current URL for this tab. The boards call this. */
export function rememberBoardUrl(route: string, url: string): void {
  try {
    sessionStorage.setItem(NAV_MEMORY_PREFIX + route, url);
  } catch {
    // A private window, or site data blocked. The nav then behaves exactly as it
    // did before this phase, which is the right degradation: a plain link.
  }
}

function rememberedBoardUrl(route: string): string | null {
  try {
    return sessionStorage.getItem(NAV_MEMORY_PREFIX + route);
  } catch {
    return null;
  }
}

function NavItem({ to, label, icon: Icon }: { to: string; label: string; icon: typeof Ticket }) {
  const { pathname } = useLocation();
  /*
   * Read on every render rather than held in state, so the entry is current the
   * moment the reader changes a filter; a `useState` would hand them the filter
   * they had two clicks ago.
   *
   * It is used only while the reader is NOT on that board. Pressing the entry for
   * the page in front of you should be the way back to the plain board - it is
   * the only way back there is - and the `title` says which of the two the link
   * will be, because a link that sometimes carries a filter has to say so.
   *
   * `NavLink`'s active state is unaffected: it compares the PATHNAME, and
   * `/tickets?companyCode=dch` is still `/tickets`.
   */
  const remembered = pathname === to ? null : rememberedBoardUrl(to);
  return (
    <NavLink
      to={remembered ?? to}
      title={remembered ? `${label} — with the filter you last used in this browser session` : undefined}
      className={({ isActive }) =>
        `flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium ${
          isActive
            ? 'bg-brand-50 text-brand-700'
            : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
        }`
      }
    >
      <Icon className="h-4 w-4" />
      {label}
    </NavLink>
  );
}


/**
 * THE SHELL, BEFORE ANYBODY KNOWS WHO IS READING (P38, task 5 - audit S2-19)
 *
 * The audit measured the first paint after an SSO return as the word `Loading...`
 * and nothing else: no rail, no header, no frame.
 *
 * What CANNOT be drawn yet is the nav, and that is not a detail to paper over -
 * which entries a person gets depends on their role, so guessing would show a
 * Viewer links they do not have and then take them away. What CAN be drawn is
 * everything true for every reader: the Dolder lockup, the app name, the rail, the
 * header band and the content frame. The nav rows are grey bars.
 *
 * It lives beside the real shell on purpose. A skeleton in its own file is a
 * second layout, and the reader sees it shift the day the two drift apart - the
 * same reason `SkeletonTable` sits next to the real table's own header list.
 */
export function AppShellSkeleton() {
  return (
    <div className="flex h-screen bg-ground">
      <aside className="flex w-60 flex-col border-r border-hair bg-surface">
        <div className="border-b border-hair px-4 py-4">
          {/* The lockup is not skeletonised: it is the one thing on screen that is
              certain, and it is what tells the reader the right app is loading. */}
          <div className="inline-block">
            <div className="text-lg font-bold leading-tight text-brand">Dolder</div>
            <div className="mt-0.5 h-[3px] bg-brand" aria-hidden="true" />
          </div>
          <div className="mt-2 text-sm font-semibold text-ink">DRA</div>
          <div className="text-xs text-muted">Digital Regulatory Assistant</div>
        </div>
        <nav className="flex-1 space-y-2 px-4 py-4" aria-busy="true">
          {Array.from({ length: 5 }).map((_, i) => <SkeletonBar key={i} className="h-4 w-full" />)}
        </nav>
      </aside>
      <div className="flex flex-1 flex-col overflow-hidden">
        <div className="flex items-center gap-4 border-b border-hair bg-surface px-6 py-2" aria-busy="true">
          {Array.from({ length: 4 }).map((_, i) => <SkeletonBar key={i} className="w-32" />)}
        </div>
        <main className="flex-1 space-y-4 overflow-auto p-6" aria-busy="true">
          <p role="status" aria-live="polite" className="sr-only">Signing in…</p>
          <SkeletonBar className="h-5 w-48" />
          <SkeletonBar className="w-2/3" />
          <SkeletonBar className="w-1/2" />
        </main>
      </div>
    </div>
  );
}

export default function Layout({ children }: { children: ReactNode }) {
  const { user, logout } = useAuth();
  const { pages } = useAccessiblePages();
  const viewablePages = new Set(pages.filter(p => p.canView).map(p => p.pageUrl));
  const navItems = NAV_ITEMS.filter(item => viewablePages.has(item.page));
  const workItems = navItems.filter(item => item.group === 'work');
  const configItems = navItems.filter(item => item.group === 'config');

  return (
    <div className="flex h-screen bg-gray-50">
      <aside className="flex w-60 flex-col border-r border-gray-200 bg-white">
        {/* THE LOCKUP (P35, reference §5).
            Until now the sidebar head was the bare string `DRA` and nothing on
            any screen said Dolder. The wordmark is `--brand` #4EA03E with a 3 px
            bar of the same colour beneath it — and that is the ONLY place the
            mark is allowed, because it measures 3.27:1 on white (Roman's
            decision D2). WCAG 1.4.3 exempts a logotype from the contrast
            requirement; nothing else in the app leans on that exemption. The
            app name below it stays ordinary dark text. */}
        <div className="border-b border-gray-200 px-4 py-4">
          <div className="inline-block">
            <div className="text-lg font-bold leading-tight text-brand">Dolder</div>
            <div className="mt-0.5 h-[3px] bg-brand" aria-hidden="true" />
          </div>
          <div className="mt-2 text-sm font-semibold text-gray-900">DRA</div>
          <div className="text-xs text-gray-500">Digital Regulatory Assistant</div>
        </div>
        {/* `justify-between` with the work group first and the config group last
            is what pushes the two apart: the gap grows with the window instead
            of being a fixed margin that looks wrong on a laptop and lost on a
            monitor. */}
        <nav className="flex flex-1 flex-col justify-between px-2 py-4">
          <div className="space-y-1">
            {workItems.map(item => <NavItem key={item.to} {...item} />)}
          </div>

          {configItems.length > 0 && (
            <div className="space-y-1">
              {/* Rule and heading belong to the group, so a role that can see
                  none of these pages gets neither. */}
              <div className="mb-2 border-t border-gray-200 pt-3">
                {/* P38, task 6: was `text-[10px]`. §3's floor is 12 px and it holds
                    for furniture as much as for content - this heading is on every
                    screen of the app. `tracking-wider` goes with the size change:
                    at 12 px the letter-spacing was what made it overflow the rail. */}
                <span className="px-3 text-xs font-semibold uppercase tracking-wide text-muted">
                  Configuration
                </span>
              </div>
              {configItems.map(item => <NavItem key={item.to} {...item} />)}
            </div>
          )}
        </nav>
        <div className="border-t border-gray-200 px-4 py-3">
          <div className="truncate text-xs text-gray-500">{user?.email}</div>
          {/* P36, S2-10: measured 64×16. The kit's quiet button is 32 px and
              carries the focus ring; the negative left margin keeps the word
              flush with the address above it, which is what the eye lines up
              on. */}
          <Button variant="quiet" size="sm" onClick={logout} className="-ml-2.5 mt-1">
            <LogOut className="h-3 w-3" />
            Sign out
          </Button>
        </div>
      </aside>
      <div className="flex flex-1 flex-col overflow-hidden">
        {/* An operating notice sits ABOVE the counters, at the top of the shell: it is about
            right now, it expires by itself, and it never reaches the What's-New panel. The bar
            renders nothing at all when there is no notice, so it costs no height. */}
        <NoticeBar />
        <OpsStrip />
        <main className="flex-1 overflow-auto p-6">{children}</main>
      </div>
    </div>
  );
}
